import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { usePhaseCollapse } from "@/hooks/usePhaseCollapse";
import PhasesHeader from "@/components/PhasesHeader";
import { api, formatApiError } from "@/lib/api";
import { projectCompletionLabel, projectStageLabel } from "@/lib/projectPresentation";
import { useAuth } from "@/context/AuthContext";
import InstallerShell from "@/components/installer/InstallerShell";
import BackButton from "@/components/BackButton";
import ScheduledVisitsSection from "@/components/installer/ScheduledVisitsSection";
import InstallerDocumentsSection from "@/components/installer/InstallerDocumentsSection";
import ProjectTasksSection from "@/components/installer/ProjectTasksSection";
import ProjectTeamPanel from "@/components/installer/ProjectTeamPanel";
import AppointmentForm from "@/components/installer/AppointmentForm";
import { LeadEngineerRow } from "@/components/installer/LeadEngineerRow";
import {
    ArrowLeft, MessageSquare, CalendarPlus, ChevronDown, ClipboardList,
    Check, Clock, Circle, Send, X, MapPin, Zap, AlertCircle, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
    not_started: { bg: "bg-[#E8E8ED]",    fg: "text-[#4B5565]", label: "Not Started", dot: "bg-[#D1D5DB]" },
    in_progress: { bg: "bg-[#FEEDE5]",    fg: "text-[#B45309]", label: "In Progress", dot: "bg-[#F26522]" },
    complete:    { bg: "bg-[#E6F1DE]",    fg: "text-[#2E7E22]", label: "Complete",    dot: "bg-[#3E9E2E]" },
};

/**
 * Installer project detail — full 1:1 with mobile
 * `app/installer/project/[id].tsx`. Contains:
 *   • Header + progress card
 *   • Message + Book visit quick-actions
 *   • Documents section (upload/toggle visibility/delete)
 *   • Scheduled Visits section (create/edit/complete/cancel + tabs)
 *   • Interactive milestones — expandable phase cards, click tasks to cycle
 */
export default function InstallerProject() {
    const { id } = useParams();
    const { user } = useAuth();
    const { hash, search } = useLocation();
    const canManageProjects = Array.isArray(user?.permissions)
        && user.permissions.includes("manage_projects");
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    // FIELD-001B1 · Was referenced by `load()` but never declared,
    // which crashed the render with `ReferenceError: loadError is not defined`.
    const [loadError, setLoadError] = useState(null);
    const { isOpen: isPhaseOpen, toggle: togglePhase, expandAll, collapseAll, allExpanded, sectionOpen, toggleSection } = usePhaseCollapse(project?.phases);
    const [msgOpen, setMsgOpen] = useState(false);
    const [msgText, setMsgText] = useState("");
    const [msgs, setMsgs] = useState([]);
    const [msgLoading, setMsgLoading] = useState(false);
    const [msgSending, setMsgSending] = useState(false);
    const [apptOpen, setApptOpen] = useState(false);
    const [engineers, setEngineers] = useState([]);
    // FIELD-001B2 · Resolve the signed-in engineer identity through the
    // authenticated `/engineers/me/workload` contract instead of the
    // roster endpoint. Ordinary Engineers are (correctly) refused by
    // `GET /engineers` (403), so relying on it left `myEngineerId` null
    // and turned every assigned canonical task into a read-only row.
    const [myEngineerId, setMyEngineerId] = useState(null);
    // FIELD-001B2 · Deep-link support for
    //   /installer/project/:id?visit=<id>#visits
    // The producer already sets both parts; InstallerProject previously
    // only read the hash so the target visit never opened.
    const focusVisitId = useMemo(() => {
        try {
            const params = new URLSearchParams(search || "");
            return params.get("visit") || null;
        } catch { return null; }
    }, [search]);
    const scrollRef = useRef(null);

    // If routed with #phases (from a progress ring) OR #visits (from the
    // Engineer Upcoming Visits list — DEV-006B), scroll to the matching
    // section once the project has loaded. Re-scroll after a short delay
    // so async child sections (Documents / Tasks / Scheduled visits) that
    // grow the page height don't leave the anchor above the viewport.
    useEffect(() => {
        if (loading || !project) return;
        const anchorId = hash === "#phases" ? "phases" : hash === "#visits" ? "visits" : null;
        if (!anchorId) return;
        const scroll = () => {
            const el = document.getElementById(anchorId);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        };
        scroll();
        const t1 = setTimeout(scroll, 400);
        const t2 = setTimeout(scroll, 1200);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [loading, project, hash]);

    // FIELD-001B2 · Only Operations (manage_projects) call the roster
    // endpoint. Ordinary Engineers must not — they get 403 back and
    // used to swallow it, leaving assigned tasks stuck read-only.
    useEffect(() => {
        if (!canManageProjects) return;
        (async () => {
            try { setEngineers(await api.listEngineers({ includeArchived: true })); }
            catch { /* best-effort */ }
        })();
    }, [canManageProjects]);

    // FIELD-001B2 · Authenticated engineer identity. The workload
    // endpoint is the single source of truth for `engineer_id` on
    // both web and mobile — see `backend/routers/engineers.py`.
    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const wl = await api.myWorkload();
                setMyEngineerId(wl?.engineer_id || null);
            } catch { setMyEngineerId(null); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.email, user?.id]);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const p = await api.getProject(id);
            setProject(p);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 404 || status === 403) {
                setLoadError("unavailable");
            } else {
                setLoadError("error");
            }
            setProject(null);
        }
        setLoading(false);
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const openChat = async () => {
        setMsgOpen(true); setMsgLoading(true);
        try { setMsgs(await api.listMessages(id)); }
        finally { setMsgLoading(false); }
    };
    useEffect(() => {
        if (msgOpen && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [msgs, msgOpen]);

    const sendMsg = async (e) => {
        e?.preventDefault();
        const t = msgText.trim();
        if (!t) return;
        setMsgSending(true);
        try {
            const saved = await api.sendMessage(id, t);
            setMsgs((prev) => [...prev, saved]);
            setMsgText("");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Send failed");
        } finally { setMsgSending(false); }
    };

    // FIELD-001B3A · The Phases accordion is a read-only phase and
    // progress overview. Row-level status transitions live on the
    // visible `ProjectTasksSection` (single Engineer task-interaction
    // surface); the previous cycleTask/NEXT_TASK_STATUS/action-button
    // path here was removed to eliminate the duplicated control.

    const grouped = useMemo(() => {
        const out = [];
        let lastDay = null;
        for (const m of msgs) {
            const day = new Date(m.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
            if (day !== lastDay) { out.push({ type: "day", key: `d-${day}`, day }); lastDay = day; }
            out.push({ type: "msg", key: m.id, msg: m });
        }
        return out;
    }, [msgs]);

    if (loading) {
        return (
            <InstallerShell>
                <div className="p-10 text-sm text-[#4B5565]" data-testid="ip-loading">Loading project…</div>
            </InstallerShell>
        );
    }
    if (!project) {
        if (loadError === "error") {
            return (
                <InstallerShell>
                    <div className="max-w-[720px] mx-auto p-8 md:p-12 text-center" data-testid="ip-load-error">
                        <div className="pe-card p-10 flex flex-col items-center gap-3">
                            <div className="w-14 h-14 rounded-full bg-[#FEECEC] text-[#DA3535] flex items-center justify-center">
                                <AlertCircle size={24} strokeWidth={1.75} />
                            </div>
                            <div className="text-xl text-[#131B2F] font-medium">We couldn't load this project</div>
                            <div className="text-sm text-[#4B5565] max-w-md">
                                Try again in a moment. If the problem persists, contact Operations.
                            </div>
                            <button onClick={load} className="pe-btn-primary text-sm mt-2" data-testid="ip-load-retry">
                                Try again
                            </button>
                        </div>
                    </div>
                </InstallerShell>
            );
        }
        return (
            <InstallerShell>
                <div className="max-w-[720px] mx-auto p-8 md:p-12 text-center" data-testid="ip-unavailable">
                    <div className="pe-card p-10 flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-[#F5F5F7] text-[#4B5565] flex items-center justify-center">
                            <AlertCircle size={24} strokeWidth={1.75} />
                        </div>
                        <div className="text-xl text-[#131B2F] font-medium">Project unavailable</div>
                        <div className="text-sm text-[#4B5565] max-w-md">
                            This project isn't in your authorised list. Ask Operations if
                            you should be assigned.
                        </div>
                        <Link
                            to="/installer/projects"
                            data-testid="ip-unavailable-back"
                            className="pe-btn-primary text-sm mt-2"
                        >
                            Back to projects
                        </Link>
                    </div>
                </div>
            </InstallerShell>
        );
    }

    return (
        <InstallerShell>
            <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-6 md:py-8" data-testid="installer-project-page">
                <div className="flex items-center gap-3 mb-6">
                    <BackButton fallbackHref="/installer/projects" testid="ip-back" size="icon" label={null} />
                    <div className="flex-1 min-w-0">
                        <div className="text-2xl md:text-3xl text-[#131B2F] font-medium tracking-tight truncate" data-testid="ip-customer">{project.customer_name}</div>
                        <div className="text-sm text-[#4B5565] mt-1 truncate">{project.system_summary}</div>
                    </div>
                </div>

                {/* Progress hero */}
                <div className="rounded-[20px] p-6 md:p-8" style={{ background: "linear-gradient(180deg,#152244 0%,#1D2E5C 55%,#1F3A78 100%)" }} data-testid="ip-progress-card">
                    <div className="text-white/60 text-xs font-medium tracking-[0.12em] uppercase">Project progress</div>
                    <div className="mt-2 flex items-baseline gap-4">
                        <div className="text-white text-5xl font-medium tracking-tight">{project.progress}%</div>
                        <div className="text-white/70 text-sm" data-testid="ip-stage">Currently: {projectStageLabel(project)}</div>
                    </div>
                    <div className="mt-4 h-2 bg-white/15 rounded-full overflow-hidden">
                        <div className="h-full bg-[#3E9E2E] transition-[width] duration-500" style={{ width: `${project.progress}%` }} />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/85">
                        <span className="inline-flex items-center gap-1.5"><MapPin size={13} strokeWidth={1.75} /> {project.address}</span>
                        <span className="inline-flex items-center gap-1.5"><Zap size={13} strokeWidth={1.75} /> {project.system_summary}</span>
                        <span data-testid="ip-est-completion" data-tone={projectCompletionLabel(project).tone}>{projectCompletionLabel(project).text}</span>
                    </div>
                    <LeadEngineerRow project={project} onAssigned={setProject} />
                </div>

                {/* Action row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
                    <button data-testid="act-msg" onClick={openChat} className="pe-card p-4 flex items-center justify-center gap-2 hover:bg-[#F5F5F7] transition-colors">
                        <MessageSquare size={18} strokeWidth={1.75} className="text-[#3E9E2E]" />
                        <span className="text-base text-[#131B2F] font-medium">Message {project.customer_name.split(" ")[0]}</span>
                    </button>
                    <button data-testid="act-appt" onClick={() => setApptOpen(true)} className="pe-card p-4 flex items-center justify-center gap-2 hover:bg-[#F5F5F7] transition-colors">
                        <CalendarPlus size={18} strokeWidth={1.75} className="text-[#3E9E2E]" />
                        <span className="text-base text-[#131B2F] font-medium">Book a visit</span>
                    </button>
                </div>

                {/* Documents */}
                <InstallerDocumentsSection projectId={project.id} />

                {/* UAT blocker fix · Site Survey card for the assigned
                    Engineer. Reuses the canonical Engineer Site Survey
                    capture route (`/installer/surveys/:projectId`) — no
                    second implementation. Read-only when no survey exists
                    (Operations creates surveys). */}
                <EngineerSiteSurveyCard project={project} userRole={user?.role} />

                {/* Tasks */}
                <div className="mt-6">
                    <ProjectTasksSection
                    projectId={project.id}
                    projects={[project]}
                    engineers={engineers}
                    leadEngineerId={project.lead_engineer_id || null}
                    myEngineerId={myEngineerId}
                />
                </div>

                {/* FIELD-001B · Read-only team panel */}
                <ProjectTeamPanel projectId={project.id} />

                {/* Scheduled visits */}
                <div id="visits" data-testid="visits-anchor">
                    <ScheduledVisitsSection
                        projectId={project.id}
                        onOpenMessages={openChat}
                        userRole={user?.role}
                        myEngineerId={myEngineerId}
                        focusVisitId={focusVisitId}
                    />
                </div>

                {/* Interactive milestones */}
                <div className="mt-8" id="phases">
                    <PhasesHeader
                        title="Phases & tasks"
                        open={sectionOpen}
                        onToggleSection={toggleSection}
                        allExpanded={allExpanded}
                        onExpandAll={expandAll}
                        onCollapseAll={collapseAll}
                        testidPrefix="ip-phases"
                    />
                    {sectionOpen && (
                    <div className="space-y-2" data-testid="phases-list">
                        {project.phases.map((phase, idx) => {
                            const s = STATUS_STYLE[phase.status];
                            const done = phase.tasks.filter((t) => t.status === "complete").length;
                            const open = isPhaseOpen(phase.id);
                            return (
                                <div key={phase.id} className="pe-card overflow-hidden" data-testid={`ip-phase-${idx}`}>
                                    <button onClick={() => togglePhase(phase.id)}
                                        data-testid={`ip-phase-toggle-${idx}`}
                                        aria-expanded={open}
                                        aria-controls={`ip-phase-tasks-${idx}`}
                                        className="w-full px-5 py-4 flex items-center gap-3 hover:bg-[#F5F5F7] transition-colors text-left">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-base text-[#131B2F] font-medium truncate">{idx + 1}. {phase.name}</div>
                                            <div className="text-sm text-[#4B5565] mt-0.5">{done}/{phase.tasks.length} tasks</div>
                                        </div>
                                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${s.bg} ${s.fg}`}>{s.label}</span>
                                        <ChevronDown
                                            size={16}
                                            strokeWidth={1.75}
                                            className={`text-[#4B5565] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                                            aria-hidden="true"
                                        />
                                    </button>
                                    {open && (
                                        <div id={`ip-phase-tasks-${idx}`} className="border-t border-[#E5E7EB] p-4 space-y-2" data-testid={`ip-phase-tasks-${idx}`}>
                                            {phase.tasks.map((t) => {
                                                const ts = STATUS_STYLE[t.status];
                                                const I = t.status === "complete" ? Check : t.status === "in_progress" ? Clock : Circle;
                                                const assigneeIds = Array.isArray(t.assignee_ids) ? t.assignee_ids : [];
                                                const isAssignedToMe = myEngineerId && assigneeIds.includes(myEngineerId);
                                                const rowTestId = `ip-task-${t.id}`;
                                                // FIELD-001B3A · Read-only phase overview. The visible
                                                // status transition lives on `ProjectTasksSection`.
                                                return (
                                                    <div key={t.id}
                                                         data-testid={rowTestId}
                                                         data-assigned-to-me={isAssignedToMe ? "true" : undefined}
                                                         data-not-assigned={!isAssignedToMe ? "true" : undefined}
                                                         className="w-full flex items-center gap-3 p-3 rounded-md opacity-95">
                                                        <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                                                            t.status === "complete" ? "bg-[#3E9E2E] text-white"
                                                            : t.status === "in_progress" ? "bg-[#F26522] text-white"
                                                            : "bg-white text-[#4B5565] border border-[#D1D5DB]"
                                                        }`}>
                                                            <I size={12} strokeWidth={2.5} />
                                                        </span>
                                                        <span className={`flex-1 text-sm ${t.status === "complete" ? "text-[#4B5565] line-through" : "text-[#131B2F]"}`}>
                                                            {t.name}
                                                        </span>
                                                        {!isAssignedToMe && (
                                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#F5F5F7] text-[#4B5565]"
                                                                  data-testid={`ip-task-not-assigned-${t.id}`}>
                                                                Not assigned to you
                                                            </span>
                                                        )}
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${ts.bg} ${ts.fg}`}>{ts.label}</span>
                                                    </div>
                                                );
                                            })}
                                            <div className="text-xs text-[#4B5565] text-center pt-2">
                                                Read-only phase overview — action controls live in the Tasks card above.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    )}
                </div>
            </div>

            {/* Real chat modal */}
            {msgOpen && (
                <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" data-testid="msg-modal">
                    <div className="bg-white rounded-2xl w-full max-w-lg h-[80vh] flex flex-col shadow-2xl">
                        <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
                            <div>
                                <div className="text-lg text-[#131B2F] font-medium">Message {project.customer_name}</div>
                                <div className="text-sm text-[#4B5565] mt-0.5">Direct chat · replies land in Sarah's mobile app</div>
                            </div>
                            <button onClick={() => setMsgOpen(false)} className="w-9 h-9 rounded-full bg-[#F5F5F7] flex items-center justify-center text-[#4B5565]" aria-label="Close">
                                <X size={16} strokeWidth={1.75} />
                            </button>
                        </div>
                        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2.5" data-testid="msg-scroll">
                            {msgLoading ? (
                                <div className="text-sm text-[#4B5565]">Loading conversation…</div>
                            ) : grouped.length === 0 ? (
                                <div className="text-sm text-[#4B5565]">No messages yet — say hi!</div>
                            ) : grouped.map((row) => {
                                if (row.type === "day") {
                                    return (
                                        <div key={row.key} className="flex items-center justify-center py-1">
                                            <span className="text-[11px] text-[#4B5565] bg-[#F5F5F7] border border-[#E5E7EB] rounded-full px-3 py-1">{row.day}</span>
                                        </div>
                                    );
                                }
                                const m = row.msg;
                                const mine = m.sender_id === user?.id;
                                return (
                                    <div key={row.key} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                                        <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${
                                            mine ? "bg-[#3E9E2E] text-white rounded-br-sm"
                                                 : "bg-[#F5F5F7] text-[#131B2F] rounded-bl-sm border border-[#E5E7EB]"
                                        }`}>
                                            {!mine && <div className="text-[11px] font-medium text-[#4B5565] mb-0.5">{m.sender_name}</div>}
                                            <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</div>
                                            <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[#4B5565]"}`}>
                                                {new Date(m.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <form onSubmit={sendMsg} className="p-3 border-t border-[#E5E7EB] flex items-center gap-2">
                            <input
                                data-testid="msg-modal-input"
                                value={msgText} onChange={(e) => setMsgText(e.target.value)}
                                placeholder="Write a message…"
                                className="flex-1 h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base outline-none focus:border-[#3E9E2E]"
                            />
                            <button data-testid="msg-send" disabled={!msgText.trim() || msgSending}
                                className="pe-btn-primary h-11 px-4 disabled:opacity-60">
                                {msgSending ? "…" : <><Send size={14} strokeWidth={2} /> Send</>}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Book visit form (real) */}
            <AppointmentForm
                open={apptOpen}
                onClose={() => setApptOpen(false)}
                projectId={project.id}
                onSaved={(_, m) => { setApptOpen(false); toast.success(m); }}
            />
        </InstallerShell>
    );
}



// ─── Engineer Site Survey card ────────────────────────────────────────
// UAT blocker fix. Deep-links to the canonical Engineer Site Survey
// capture screen already delivered in DEV-010B-2 (`/installer/surveys/:pid`).
// This card only reads the current status — Operations owns creation, so
// Engineers see either the current survey or a read-only "no survey yet"
// hint. The card is intentionally compact and lives alongside Documents,
// Tasks, Scheduled Visits so the whole lifecycle is on one screen.

const SURVEY_STATUS_STYLE = {
    not_started: {
        label: "Not started",
        headline: "Site survey — Not started",
        action: "Open survey",
        tint: "bg-[#F1F5F9] text-[#334155]",
        icon: ClipboardList,
    },
    in_progress: {
        label: "In progress",
        headline: "Site survey — In progress",
        action: "Continue survey",
        tint: "bg-[#FEF3C7] text-[#92400E]",
        icon: Clock,
    },
    returned_for_clarification: {
        label: "Clarification required",
        headline: "Clarification required",
        action: "Review & amend",
        tint: "bg-[#FEE2E2] text-[#991B1B]",
        icon: AlertCircle,
    },
    submitted: {
        label: "Submitted",
        headline: "Submitted for review",
        action: "View submitted survey",
        tint: "bg-[#DBEAFE] text-[#1E40AF]",
        icon: Check,
    },
    approved: {
        label: "Approved",
        headline: "Approved",
        action: "View approved survey",
        tint: "bg-[#DCFCE7] text-[#166534]",
        icon: Check,
    },
};

function EngineerSiteSurveyCard({ project, userRole }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [survey, setSurvey] = useState(null);

    useEffect(() => {
        // Customers must never call the Engineer surveys endpoint — 403.
        if (userRole === "customer") { setLoading(false); return; }
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const rows = await api.listMySurveys();
                if (cancelled) return;
                const found = (rows || []).find((s) => s.project_id === project.id);
                setSurvey(found || null);
            } catch {
                if (!cancelled) setSurvey(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [project.id, userRole]);

    if (userRole === "customer") return null;

    const status = survey?.status;
    // Legacy surveys may not carry a `returned_for_clarification` status
    // but do carry unresolved clarification records. Treat those the same.
    const hasOpenClarification = Array.isArray(survey?.clarifications)
        && survey.clarifications.some((c) => !c.resolved_at);
    const effective = hasOpenClarification && status !== "approved"
        ? "returned_for_clarification"
        : status;
    const meta = SURVEY_STATUS_STYLE[effective] || SURVEY_STATUS_STYLE.not_started;
    const Icon = meta.icon;

    return (
        <div className="mt-6" data-testid="ip-site-survey-section">
            <div className="pe-card overflow-hidden">
                <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center gap-3">
                    <span className="w-9 h-9 rounded-md bg-[#F1F8EC] text-[#3E9E2E] flex items-center justify-center shrink-0">
                        <ClipboardList size={17} strokeWidth={1.75} />
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="text-base text-[#131B2F] font-medium">Site Survey</div>
                        <div className="text-xs text-[#4B5565] mt-0.5">
                            Digital survey capture · owned by the assigned Engineer
                        </div>
                    </div>
                </div>
                <div className="p-5">
                    {loading ? (
                        <div className="text-sm text-[#4B5565]" data-testid="ip-site-survey-loading">
                            Loading site survey…
                        </div>
                    ) : !survey ? (
                        <div
                            data-testid="ip-site-survey-none"
                            className="flex items-start gap-3 rounded-md border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3"
                        >
                            <Circle size={16} strokeWidth={1.75} className="text-[#4B5565] mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-[#131B2F] font-medium">No site survey created</div>
                                <div className="text-xs text-[#4B5565] mt-0.5">
                                    Operations will create the survey record for this project.
                                    You&rsquo;ll see it here as soon as it&rsquo;s available.
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col md:flex-row md:items-center gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span
                                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.tint}`}
                                        data-testid="ip-site-survey-status"
                                    >
                                        <Icon size={11} strokeWidth={2} /> {meta.label}
                                    </span>
                                </div>
                                <div className="mt-1.5 text-sm text-[#131B2F] font-medium" data-testid="ip-site-survey-headline">
                                    {meta.headline}
                                </div>
                                {survey.appointment_date && (
                                    <div className="mt-0.5 text-xs text-[#4B5565]">
                                        Linked visit · {new Date(survey.appointment_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate(`/installer/surveys/${project.id}`)}
                                data-testid="ip-site-survey-open"
                                className="pe-btn-primary h-10 px-4 shrink-0 inline-flex items-center gap-1.5"
                            >
                                {meta.action} <ChevronRight size={14} strokeWidth={2} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
